// @ts-check
// Formulários do atendimento (contrato de bots, seções 4.11 a 4.13 e 13): definir a finalidade da conversa, abrir
// atendimento (departamento, assunto e resumo com variáveis, pedido ou caso de retenção) e transferir para uma pessoa
// (departamento, membro, consultor do card ou rodízio; textos de departamento aberto e fechado; resumo). Ouvidoria e
// Proteção nunca são destino.
import { html } from 'htm/preact';
import { SegmentedControl } from '../../../ui/segmented-control.js';
import { BOT_HANDOFF_TARGET_KINDS, BOT_TICKET_TYPES } from '../../../../../shared/bots/bot-definition.js';
import { FlowNotice } from '../../flows/flow-ui.js';
import { BotMembersField, BotSelectField, BotTextField } from '../bot-form-fields.js';
import { handoffTeams, memberChoices } from '../bot-lookups.js';
import { botFieldProblemText, botProblemText } from '../bot-ui.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {import('../bot-node-panel.js').BotNodeFormProps} BotNodeFormProps
 * @typedef {import('../bot-lookups.js').BotLookupData} BotLookupData
 */

/** @param {BotLookupData} lookupData */
const teamOptions = (lookupData) => handoffTeams(lookupData.teams).map((team) => ({ value: team.id, label: team.name }));

/**
 * Destino da transferência (bloco "Transferir" e o "Falar com uma pessoa" das configurações).
 * @param {{ target: Record<string, any> | null | undefined, onChange: (target: Record<string, any>) => void, lookupData: BotLookupData,
 *   problems: import('../../../lib/bot-validation.js').BotEditorProblem[], fieldPrefix?: string }} props
 */
export function HandoffTargetFields({ target, onChange, lookupData, problems, fieldPrefix = 'target' }) {
  const kind = target?.kind ?? 'TEAM';
  const restricted = problems.find((problem) => problem.code === 'HANDOFF_RESTRICTED_TEAM');
  return html`<div class="bot-handoff-target">
    <${BotSelectField} label=${t('bots.forms.handoff.targetKind')} value=${kind} placeholder=${null}
      options=${BOT_HANDOFF_TARGET_KINDS.map((value) => ({ value, label: t(`bots.handoffTargets.${value}`) }))}
      onChange=${(value) => onChange(value === 'TEAM' ? { kind: 'TEAM', teamId: null } : value === 'MEMBER' ? { kind: 'MEMBER', workspaceMemberId: null }
        : value === 'ROUND_ROBIN' ? { kind: 'ROUND_ROBIN', workspaceMemberIds: [] } : { kind: 'CARD_OWNER' })} />
    ${kind === 'TEAM'
      ? html`<${BotSelectField} label=${t('bots.forms.team')} value=${target?.teamId} placeholder=${t('bots.forms.chooseTeam')} options=${teamOptions(lookupData)}
          onChange=${(teamId) => onChange({ kind: 'TEAM', teamId })} error=${botFieldProblemText(problems, `${fieldPrefix}.teamId`) ?? (restricted ? botProblemText(restricted) : null)}
          testId="bot-handoff-team" />`
      : null}
    ${kind === 'MEMBER'
      ? html`<${BotSelectField} label=${t('bots.forms.member')} value=${target?.workspaceMemberId} placeholder=${t('bots.forms.chooseMember')}
          options=${memberChoices().map((member) => ({ value: member.id, label: member.label }))}
          onChange=${(workspaceMemberId) => onChange({ kind: 'MEMBER', workspaceMemberId })} error=${botFieldProblemText(problems, `${fieldPrefix}.workspaceMemberId`)} />`
      : null}
    ${kind === 'ROUND_ROBIN'
      ? html`<${BotMembersField} label=${t('bots.forms.members')} members=${memberChoices()} selected=${target?.workspaceMemberIds ?? []}
          onChange=${(workspaceMemberIds) => onChange({ kind: 'ROUND_ROBIN', workspaceMemberIds })} error=${botFieldProblemText(problems, `${fieldPrefix}.workspaceMemberIds`)} />`
      : null}
    ${kind === 'CARD_OWNER' ? html`<p class="flow-field-hint">${t('bots.forms.handoff.cardOwnerHint')}</p>` : null}
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function SetPurposeForm({ node, problems, lookupData, onConfig }) {
  const config = node.config ?? {};
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-set-purpose-form">
    <div class="flow-field">
      <span class="flow-field-label">${t('bots.forms.setPurpose.purpose')}</span>
      <${SegmentedControl} label=${t('bots.forms.setPurpose.purpose')} value=${config.purpose ?? 'SERVICE'}
        options=${['CAPTURE', 'SERVICE'].map((value) => ({ value, label: t(`bots.threadPurposes.${value}`) }))} onChange=${(/** @type {string} */ purpose) => onConfig({ purpose })} />
    </div>
    ${config.purpose === 'SERVICE'
      ? html`<${BotSelectField} label=${t('bots.forms.setPurpose.team')} value=${config.teamId} placeholder=${t('bots.forms.setPurpose.noTeam')} options=${teamOptions(lookupData)}
          onChange=${(teamId) => onConfig({ teamId })} error=${botFieldProblemText(problems, 'teamId')} />`
      : null}
    <${FlowNotice}>${t('bots.forms.setPurpose.notice')}<//>
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function OpenTicketForm({ node, problems, variables, lookupData, onConfig }) {
  const config = node.config ?? {};
  const restricted = problems.find((problem) => problem.code === 'HANDOFF_RESTRICTED_TEAM');
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-open-ticket-form">
    <div class="flow-field">
      <span class="flow-field-label">${t('bots.forms.openTicket.type')}</span>
      <${SegmentedControl} label=${t('bots.forms.openTicket.type')} value=${config.ticketType ?? 'REQUEST'}
        options=${BOT_TICKET_TYPES.map((value) => ({ value, label: t(`bots.ticketTypes.${value}`) }))} onChange=${(/** @type {string} */ ticketType) => onConfig({ ticketType })} />
    </div>
    <${BotSelectField} label=${t('bots.forms.team')} value=${config.teamId} placeholder=${t('bots.forms.chooseTeam')} options=${teamOptions(lookupData)}
      onChange=${(teamId) => onConfig({ teamId })} error=${botFieldProblemText(problems, 'teamId') ?? (restricted ? botProblemText(restricted) : null)} testId="bot-ticket-team" />
    <${BotTextField} label=${t('bots.forms.openTicket.subject')} value=${config.subjectTemplate} variables=${variables} maximum=${200}
      onChange=${(subjectTemplate) => onConfig({ subjectTemplate })} error=${botFieldProblemText(problems, 'subjectTemplate')} />
    <${BotTextField} label=${t('bots.forms.openTicket.summary')} value=${config.summaryTemplate} variables=${variables} multiline maximum=${2000}
      hint=${t('bots.forms.openTicket.summaryHint')} onChange=${(summaryTemplate) => onConfig({ summaryTemplate: summaryTemplate || null })} />
    <${FlowNotice}>${config.ticketType === 'RETENTION' ? t('bots.forms.openTicket.retentionNotice') : t('bots.forms.openTicket.notice')}<//>
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function HandoffForm({ node, problems, variables, lookupData, purpose, onConfig }) {
  const config = node.config ?? {};
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-handoff-form">
    <${HandoffTargetFields} target=${config.target} lookupData=${lookupData} problems=${problems} onChange=${(target) => onConfig({ target })} />
    <${BotTextField} label=${t('bots.forms.handoff.openText')} value=${config.openText} variables=${variables} multiline
      placeholder=${purpose === 'CAPTURE' ? t('bots.defaultTexts.captureHandoff') : t('bots.defaultTexts.handoffOpen')} onChange=${(openText) => onConfig({ openText: openText || null })} />
    ${config.target?.kind === 'TEAM' || config.target?.kind === 'MEMBER'
      ? html`<${BotTextField} label=${t('bots.forms.handoff.closedText')} value=${config.closedText} variables=${variables} multiline
          placeholder=${t('bots.defaultTexts.handoffClosed')} onChange=${(closedText) => onConfig({ closedText: closedText || null })} />`
      : null}
    <${BotTextField} label=${t('bots.forms.handoff.summary')} value=${config.summaryTemplate} variables=${variables} multiline maximum=${2000}
      hint=${t('bots.forms.handoff.summaryHint')} onChange=${(summaryTemplate) => onConfig({ summaryTemplate: summaryTemplate || null })} />
    <${FlowNotice}>${t('bots.forms.handoff.notice')}<//>
  </div>`;
}
